import {
  Badge,
  createTheme,
  InputLabel,
  Text,
  Title,
  type CSSProperties,
  type MantineTheme,
  type TextProps,
  type TitleProps,
} from "@mantine/core";
import { BRAND_SHADES, INK_SHADES } from "./colors";

const FONT_FAMILY =
  "General Sans, Outfit, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji";
const DISPLAY_FONT_FAMILY = `Archivo, ${FONT_FAMILY}`;

const getDisplayFrontStyle = (props: TitleProps) => {
  const { size, order } = props;

  const baseStyle = {
    fontFamily: DISPLAY_FONT_FAMILY,
    fontWeight: 800,
    color: INK_SHADES[900],
  };

  const xlStyle = {
    ...baseStyle,
    letterSpacing: "-0.02em",
    lineHeight: "1.05",
    fontSize: "64px",
  };

  const lStyle = {
    ...baseStyle,
    letterSpacing: "-0.015em",
    lineHeight: "1.1",
    fontSize: "44px",
  };

  const mStyle = {
    ...baseStyle,
    letterSpacing: "-0.01em",
    lineHeight: "1.15",
    fontSize: "28px",
  };

  if (
    Number.isInteger(size) ||
    (typeof size === "string" && Number.isInteger(Number(size.split("px")[0])))
  ) {
    const sizeInPx =
      typeof size === "string" ? Number(size.split("px")[0]) : Number(size);

    if (sizeInPx >= 64) {
      return { ...xlStyle, fontSize: `${sizeInPx}px` };
    } else if (sizeInPx >= 44) {
      return { ...lStyle, fontSize: `${sizeInPx}px` };
    } else {
      return { ...mStyle, fontSize: `${sizeInPx}px` };
    }
  }

  switch (size) {
    case "xl":
      return xlStyle;
    case "lg":
      return lStyle;
    case "md":
    case "sm": // Display cannot be smaller than 'md'
    case "xs":
      return mStyle;
  }

  // If no sizes specified we base on the given order
  switch (order) {
    case 1:
      return xlStyle;
    case 2:
      return lStyle;
    case 3:
    case 4:
    case 5:
    case 6:
      return mStyle;
  }

  return xlStyle; // Default to 'xl' style if no size or order is specified
};

const getTitleFrontStyle = (props: TitleProps) => {
  if (props.variant === "display") {
    return getDisplayFrontStyle(props);
  }

  if (props.order === 6) {
    return {
      letterSpacing: "0.02em",
    };
  }

  // Standard Title sizes are handled by theme.headings.sizes.
  return {};
};

const getTextFrontStyle = (theme: MantineTheme, props: TextProps) => {
  if (props.variant === "accent") {
    return {
      fontStyle: "inherit",
      color: "inherit",
      lineHeight: "inherit",
      boxShadow: `inset 0 -0.24em 0 ${BRAND_SHADES[100]}`,
    };
  }

  let style: CSSProperties = {
    color: INK_SHADES[700],
    lineHeight: "23px",
  };

  if (props.variant === "caption") {
    style = {
      ...style,
      fontSize: "11.5px",
      lineHeight: "1.45",
      color: INK_SHADES[500],
    };
  }

  if (props.variant === "eyebrow") {
    style = {
      ...style,
      fontSize: "11px",
      letterSpacing: "0.1em",
      color: BRAND_SHADES[500],
      textTransform: "uppercase",
      fontWeight: 500,
    };
  }

  if (props.variant === "overline") {
    style = {
      ...style,
      fontSize: "11px",
      lineHeight: "1.45",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      fontWeight: theme.fontWeights.bold,
      color: INK_SHADES[500],
    };
  }

  if (props.variant === "strong" || props.variant === "numeric") {
    style = {
      ...style,
      fontSize: theme.fontSizes.sm,
      fontWeight: theme.fontWeights.bold,
      color: INK_SHADES[900],
    };
  }

  if (props.variant === "numeric") {
    style = {
      ...style,
      fontVariantNumeric: "tabular-nums",
    };
  }

  if (props.size === "sm") {
    style = {
      ...style,
      fontSize: theme.fontSizes.sm,
      lineHeight: "1.5",
      color: INK_SHADES[600],
    };
  }

  return style;
};

export const TypographyTheme = createTheme({
  components: {
    Title: Title.extend({
      styles: (_theme, props) => ({
        root: getTitleFrontStyle(props),
      }),
    }),

    Text: Text.extend({
      defaultProps: {
        size: "md",
      },
      styles: (theme, props) => ({
        root: getTextFrontStyle(theme, props),
      }),
    }),
    InputLabel: InputLabel.extend({
      defaultProps: {
        fw: 600,
      },
    }),
    Badge: Badge.extend({
      styles: (_theme, props) =>
        props.variant === "tag"
          ? {
              root: {
                flexShrink: 0,
                height: 18,
                paddingInline: 7,
                fontSize: 10,
                gridTemplateColumns: "auto",
                textTransform: "uppercase",
              },
              section: { display: "none" },
              label: {
                textTransform: "inherit",
                fontWeight: 600,
                letterSpacing: "0.03em",
              },
            }
          : {
              root: { flexShrink: 0 },
              label: { textTransform: "none", fontWeight: 500 },
            },
    }),
  },

  fontFamily: FONT_FAMILY,
  fontWeights: {
    regular: "400",
    medium: "500",
    bold: "600",
  },
  fontSizes: {
    sm: "13px",
    md: "15px",
  },
  headings: {
    fontFamily: FONT_FAMILY,
    fontWeight: "600",
    sizes: {
      h1: {
        fontSize: "28px",
        lineHeight: "1.25",
        fontWeight: "600",
      },
      h2: {
        fontSize: "20px",
        lineHeight: "1.3",
        fontWeight: "600",
      },
      h3: {
        fontSize: "16px",
        lineHeight: "1.35",
        fontWeight: "600",
      },
      h4: {
        fontSize: "14px",
        lineHeight: "1.4",
        fontWeight: "600",
      },
      h5: {
        fontSize: "13px",
        lineHeight: "1.45",
        fontWeight: "600",
      },
      h6: {
        fontSize: "12px",
        lineHeight: "1.45",
        fontWeight: "600",
      },
    },
  },
});
